import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Add, AddCircle, Delete, LocationOnOutlined, Remove } from "@mui/icons-material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { CenterEndPoints, createGeneralAPIEndpoint } from "../../../../services/General";

// ✅ Validation schema
const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  centerId: yup.string().required("CenterId ID is required"),
  opensAtUtc: yup
    .date()
    .required("Open date is required")
    .typeError("Invalid date format"),
  closesAtUtc: yup
    .date()
    .required("Close date is required")
    .typeError("Invalid date format")
    .min(yup.ref("opensAtUtc"), "Close date must be after open date"),
  durationMinutes: yup
    .number()
    .typeError("Must be a number")
    .required("Duration Minutes required")
    .min(15, "Duration Minutes be at least 15 minutes"),
  questions: yup
    .array()
    .of(
      yup.object().shape({
        questionText: yup.string().required("Question text is required"),
        options: yup
          .array()
          .of(yup.string().required("Option cannot be empty"))
          .min(2, "At least two options required"),
        correctOptionsIndex: yup
          .number()
          .typeError("Must be a number")
          .required("Correct option is required")
          .min(0, "Invalid correct option index"),
        points: yup
          .number()
          .typeError("Must be a number")
          .required("Points are required")
          .min(1, "Points must be between 1 and 10")
          .max(10, "Points must be between 1 and 10"),
      }),
    )
    .min(1, "At least one question is required"),
});
function AddExamDialog({ open, onClose, onSubmit }) {
  const [centers, setCenters] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      type: 1,
      centerId: 0,
      opensAtUtc: "",
      closesAtUtc: "",
      durationMinutes: 15,
      allowMultipleSubmissions: false,
      questions: [
        {
          questionText: "",
          options: ["", "", "", ""],
          correctOptionsIndex: 0,
          points: 1,
        },
      ],
    },
  });

  useEffect(() => {
    if (open) {
      createGeneralAPIEndpoint(CenterEndPoints.all)
        .fetch()
        .then((res) => setCenters(res.data))
        .catch((err) => {
          console.error("Failed to fetch centers:", err);
          enqueueSnackbar("Failed to fetch centers.", { variant: "error" });
        }); 
    }
  }, [open, enqueueSnackbar]);

  const {
    fields: questions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "questions",
  });

  const handleFormSubmit = (data) => {
    onSubmit(data);
    reset();
  };
  const handleClose = () => {
    reset();
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      className="add-assessment dialog"
    >
      <DialogTitle className="title">Create Exam</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent dividers>
          <Box className="box-container">
            <Box className="box-items">
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField"
                    {...field}
                    label="Title"
                    margin="normal"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="centerId"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField select"
                    {...field}
                    select
                    label="Center"
                    margin="normal"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    {centers.map((center) => (
                      <MenuItem
                        key={center.id}
                        value={center.id}
                        className="center-menu-item"
                      >
                        <div className="center-option">
                          <span className="center-title">
                            <span className="icon">
                              <LocationOnOutlined />
                            </span>
                            {center.name}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField"
                    {...field}
                    margin="normal"
                    label="Description"
                    multiline
                    rows={2}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="opensAtUtc"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    className="textField"
                    type="datetime-local"
                    margin="normal"
                    label="Opens At"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="closesAtUtc"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    className="textField"
                    type="datetime-local"
                    margin="normal"
                    label="Closes At"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />

              <Controller
                name="durationMinutes"
                control={control}
                render={({ field, fieldState }) => (
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      onClick={() =>
                        field.onChange(Math.max(1, field.value - 1))
                      }
                    >
                      <Remove />
                    </IconButton>
                    <TextField
                      {...field}
                      type="number"
                      className="textField"
                      label="Duration Minutes"
                      fullWidth
                      margin="normal"
                      helperText={fieldState.error?.message}
                      error={!!fieldState.error}
                      inputProps={{
                        min: 1,
                        readOnly: true,
                      }}
                    />

                    <IconButton onClick={() => field.onChange(field.value + 1)}>
                      <Add />
                    </IconButton>
                  </Box>
                )}
              />
            </Box>

            {/* Questions Section */}
            <Box>
              <span className="question-header">Questions</span>
              {questions.map((q, qIndex) => (
                <Box
                  key={q.id}
                  sx={{
                    border: "1px solid #ddd",
                    p: 1,
                    mb: 2,
                    mt: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box>
                    <Controller
                      name={`questions.${qIndex}.questionText`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={`Question ${qIndex + 1}`}
                          margin="normal"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Box>

                  {/* Options */}
                  <Box>
                    <Controller
                      name={`questions.${qIndex}.correctOptionsIndex`}
                      control={control}
                      render={({ field: radioField, fieldState }) => (
                        <RadioGroup
                          value={radioField.value}
                          onChange={(e) =>
                            radioField.onChange(Number(e.target.value))
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        >
                          {q.options.map((_, optIndex) => (
                            <Box
                              key={optIndex}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 1,
                              }}
                            >
                              <FormControlLabel
                                value={optIndex}
                                control={<Radio />}
                                label=""
                                sx={{ m: 0 }}
                              />

                              <Controller
                                name={`questions.${qIndex}.options.${optIndex}`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    label={`Option ${optIndex + 1}`}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </Box>

                  <Box className="form-grid-item">
                    <Controller
                      name={`questions.${qIndex}.points`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            onClick={() =>
                              field.onChange(
                                Math.max(1, Number(field.value) - 1),
                              )
                            }
                          >
                            <Remove />
                          </IconButton>
                          <TextField
                            {...field}
                            type="number"
                            label="Points"
                            fullWidth
                            margin="normal"
                            className="textField"
                            helperText={fieldState.error?.message}
                            error={!!fieldState.error}
                            inputProps={{
                              min: 1,
                              max: 10,
                              readOnly: true,
                            }}
                          />

                          <IconButton
                            onClick={() =>
                              field.onChange(Number(field.value) + 1)
                            }
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      )}
                    />
                  </Box>

                  <IconButton color="error" onClick={() => remove(qIndex)}>
                    <Delete />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddCircle />}
                onClick={() =>
                  append({
                    questionText: "",
                    options: ["", "", "", ""],
                    correctOptionsIndex: 0,
                    points: 1,
                  })
                }
              >
                Add Question
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddExamDialog;
